import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from "axios";
import {toast, ToastContainer} from "react-toastify";

const DynamicFormComponent = ({ fields, externalValues = {}, onSuccess, onError, onBeforeSubmit }) => {
    // Initialize form values properly, handling dates and numbers
    const initializeFormValues = () => {
        const values = {};
        fields.forEach(field => {
            if (externalValues[field.name] !== undefined) {
                // Handle date fields specially
                if (field.type === 'date' && externalValues[field.name]) {
                    try {
                        const dateValue = new Date(externalValues[field.name]);
                        values[field.name] = !isNaN(dateValue.getTime()) ? dateValue : null;
                    } catch (e) {
                        values[field.name] = null;
                    }
                } else if (field.type === 'number' && externalValues[field.name] !== null) {
                    // Handle number fields
                    const numValue = Number(externalValues[field.name]);
                    values[field.name] = !isNaN(numValue) ? numValue : '';
                } else {
                    values[field.name] = externalValues[field.name];
                }
            } else {
                // Handle default values for date fields
                if (field.type === 'date' && field.defaultValue) {
                    try {
                        const dateValue = new Date(field.defaultValue);
                        values[field.name] = !isNaN(dateValue.getTime()) ? dateValue : null;
                    } catch (e) {
                        values[field.name] = null;
                    }
                } else if (field.type === 'number' && field.defaultValue !== undefined) {
                    // Handle default values for number fields
                    const numValue = Number(field.defaultValue);
                    values[field.name] = !isNaN(numValue) ? numValue : '';
                } else {
                    values[field.name] = field.defaultValue || '';
                }
            }
        });
        return values;
    };

    const [formValues, setFormValues] = useState(initializeFormValues());
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [submitBtnTxt, setSubmitBtnTxt] = useState('Submit');
    const today = new Date();

    useEffect(() => {
        setFormValues(prevValues => {
            const newValues = { ...prevValues };

            // Update with external values, handling dates and numbers properly
            Object.keys(externalValues).forEach(key => {
                const value = externalValues[key];

                // If it's a date field and the value is a string, convert to Date
                const dateField = fields.find(field => field.name === key && field.type === 'date');
                if (dateField && value && typeof value === 'string') {
                    try {
                        const dateValue = new Date(value);
                        if (!isNaN(dateValue.getTime())) {
                            newValues[key] = dateValue;
                        } else {
                            newValues[key] = value;
                        }
                    } catch (e) {
                        newValues[key] = value;
                    }
                } else if (fields.find(field => field.name === key && field.type === 'number') && value !== null) {
                    // Handle number fields
                    const numValue = Number(value);
                    newValues[key] = !isNaN(numValue) ? numValue : value;
                } else {
                    newValues[key] = value;
                }
            });

            return newValues;
        });
    }, [externalValues, fields]);

    const handleChange = (name, value) => {
        // Find the field configuration
        const field = fields.find(f => f.name === name);

        if(name === 'PeakItem') {
            const trimmedValue = value.trim();
            setFormValues({ ...formValues, [name]: trimmedValue, ['SupplierItem']: trimmedValue });
            return;
        }

        // Format the dollar amount
        if (field && field.type === 'number') {
            // Handle number fields with validation
            if (value === '' || value === null) {
                setFormValues({ ...formValues, [name]: '' });
            } else {
                const numericValue = Number(value);
                if (!isNaN(numericValue)) {
                    // Apply min/max constraints if specified
                    let finalValue = numericValue;
                    if (field.min !== undefined && numericValue < field.min) {
                        finalValue = field.min;
                    }
                    if (field.max !== undefined && numericValue > field.max) {
                        finalValue = field.max;
                    }
                    setFormValues({ ...formValues, [name]: finalValue });
                } else {
                    // If not a valid number, keep the previous value
                    return;
                }
            }
        } else {
            const updatedValues = { ...formValues, [name]: value };
            
            // Handle conditional fields - clear dependent field if parent changes
            if (field && field.showConditionalField) {
                const conditionalFieldName = field.showConditionalField.fieldName;
                // Clear the conditional field if the trigger value doesn't match
                if (value !== field.showConditionalField.triggerValue) {
                    updatedValues[conditionalFieldName] = '';
                }
            }
            
            setFormValues(updatedValues);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const fieldName = event.target.name;
        setFileError('');

        if (file) {
            // Validate file type
            const allowedTypes = ['application/pdf', 'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'text/plain', 'image/jpeg', 'image/png', 'image/gif'];

            if (!allowedTypes.includes(file.type)) {
                setFileError('Please select a valid file type (PDF, DOC, DOCX, TXT, JPG, PNG, GIF)');
                setSelectedFile(null);
                // Clear the form value for this field
                setFormValues(prev => ({ ...prev, [fieldName]: null }));
                return;
            }

            // Validate file size (5MB limit)
            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
            if (file.size > maxSize) {
                setFileError('File size must be less than 5MB');
                setSelectedFile(null);
                // Clear the form value for this field
                setFormValues(prev => ({ ...prev, [fieldName]: null }));
                return;
            }

            setSelectedFile(file);
            // Store the file in form values
            setFormValues(prev => ({ ...prev, [fieldName]: file }));
        } else {
            setSelectedFile(null);
            setFormValues(prev => ({ ...prev, [fieldName]: null }));
        }
    };

    const openPage = () => {
        window.location.href = "/suppliers-data";
    }

    const handleDateChange = (name, date) => {
        // Format date to ISO string that the backend expects
        if (date && date instanceof Date && !isNaN(date.getTime())) {
            const isoString = date.toISOString();
            setFormValues({ ...formValues, [name]: date }); // Store the Date object for DatePicker
        } else {
            setFormValues({ ...formValues, [name]: null });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitBtnTxt('Submitting...')
        // Call custom validation if provided
        if (onBeforeSubmit && !onBeforeSubmit(formValues)) {
            return; // Stop submission if validation fails
        }

        // Check if required text and number fields are filled
        const requiredFields = fields.filter(field => {
            // Only check fields that are currently visible and required
            if (!field.required || field.type === 'file' || !shouldShowField(field)) {
                return false;
            }
            
            // For conditional fields, check if they should be required based on visibility
            if (field.showWhen) {
                const parentFieldValue = formValues[field.showWhen.field];
                return parentFieldValue === field.showWhen.value;
            }
            
            return true;
        });
        for (const field of requiredFields) {
            const value = formValues[field.name];
            if (field.type === 'number') {
                // For number fields, check if it's a valid number
                if (value === '' || value === null || value === undefined || isNaN(Number(value))) {
                    setSubmitBtnTxt('Submit');
                    toast.warning(`Please enter a valid number for ${field.label}`);
                    return;
                }
            } else if (!value || (typeof value === 'string' && value.trim() === '')) {
                setSubmitBtnTxt('Submit');
                toast.warning(`Please fill in ${field.label}`);
                return;
            }
        }

        // Check if required file fields are present
        const requiredFileFields = fields.filter(field => {
            if (field.type !== 'file' || !field.required || !shouldShowField(field)) {
                return false;
            }
            
            // For conditional file fields, check if they should be required based on visibility
            if (field.showWhen) {
                const parentFieldValue = formValues[field.showWhen.field];
                return parentFieldValue === field.showWhen.value;
            }
            
            return true;
        });
        for (const field of requiredFileFields) {
            const fileValue = formValues[field.name];
            if (!fileValue || !(fileValue instanceof File)) {
                toast.warning(`Please select a file for ${field.label}`);
                setSubmitBtnTxt('Submit');
                return;
            }
        }

        try {
            const formData = new FormData();

            // Append all form values
            Object.keys(formValues).forEach(key => {
                const value = formValues[key];
                if (value !== null && value !== undefined) {
                    // If it's a file, append it directly
                    if (value instanceof File) {
                        formData.append(key, value);
                    } else if (value instanceof Date) {
                        // Format as YYYY-MM-DD
                        const year = value.getFullYear();
                        const month = String(value.getMonth() + 1).padStart(2, '0');
                        const day = String(value.getDate()).padStart(2, '0');
                        formData.append(key, `${year}-${month}-${day}`);
                    } else {
                        // For other values, convert to string
                        formData.append(key, value.toString());
                    }
                }
            });

            console.log('Form submitted with values:', formValues);
            console.log('FormData contents:');
            for (let pair of formData.entries()) {
                console.log(pair[0], pair[1]);
            }

            const response = await axios.post('add-supplier', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            // Check if response indicates success
            if (response.data.resp === 'OK' || response.status === 200) {
                debugger;
                toast.success('Record Added Successfully!');
                setSubmitBtnTxt('Submitted');
                
                // Call success callback if provided
                if (onSuccess) {
                    onSuccess(response.data);
                }
                
                // Use a more user-friendly delay and provide feedback
                setTimeout(() => {
                    openPage();
                }, 1500); // Shorter delay
                
            } else {
                throw new Error(response.data.error || 'Unknown error occurred');
            }

        } catch (error) {
            console.error('There was an error!', error);
            toast.error('Error submitting form. Please try again.');

            // Call error callback if provided
            if (onError) {
                onError(error);
            }
        }
    };

    // Helper function to check if a field should be shown
    const shouldShowField = (field) => {
        if (field.hidden) return false;
        
        // Check if this is a conditional field
        if (field.showWhen) {
            const parentFieldValue = formValues[field.showWhen.field];
            return parentFieldValue === field.showWhen.value;
        }
        
        return true;
    };

    return (
        <div className="container">
            <form onSubmit={handleSubmit}>
                {fields.map(field => (
                    <div key={field.name} className="mb-3" style={{display: shouldShowField(field) ? 'block' : 'none'}}>
                        <label htmlFor={field.name} className="form-label">
                            {field.label} 
                            {field.required && <span className="text-danger"> * </span>}
                            <span style={{fontSize:'12px', color:'blue'}}>{field.note}</span>
                            
                        </label>
                        {field.type === 'date' ? (
                            <DatePicker
                                className="form-control"
                                id={field.name}
                                name={field.name}
                                minDate={today}
                                selected={formValues[field.name]}
                                onChange={(date) => handleDateChange(field.name, date)}
                                dateFormat="MM-dd-yyyy"
                                required={field.required && shouldShowField(field)} // Only required if visible
                            />
                        ) : field.type === 'select' ? (
                            <div className="input-group">
                                {field.icon && (
                                    <span className="input-group-text">
                                        <i style={{fontSize:'18px'}} className={field.icon}></i>
                                    </span>
                                )}
                                <select
                                    className="form-control form-select"
                                    id={field.name}
                                    name={field.name}
                                    value={formValues[field.name] || ''}
                                    onChange={(e) => handleChange(field.name, e.target.value)}
                                    required={field.required && shouldShowField(field)} // Only required if visible
                                >
                                    <option value="">{field.placeholder || `Select ${field.label}`}</option>
                                    {field.options && field.options.map((option, index) => (
                                        <option key={index} value={option.value || option}>
                                            {option.label || option}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : field.type === 'file' ? (
                            <div>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i style={{fontSize:'18px'}} className="fa-solid fa-file-arrow-up"></i>
                                    </span>
                                    <input
                                        type="file"
                                        className="form-control"
                                        id={field.name}
                                        name={field.name}
                                        onChange={handleFileChange}
                                        accept={field.accept || ".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"}
                                        required={field.required && shouldShowField(field)} // Only required if visible
                                    />
                                </div>
                                {field.helpText && (
                                    <div className={`mt-1 ${field.required ? 'text-danger' : 'text-muted'}`} style={{fontSize: '12px'}}>
                                        {field.required && <span className="fw-bold">* </span>}
                                        {field.helpText}
                                    </div>
                                )}
                                {fileError && (
                                    <div className="text-danger mt-1" style={{fontSize: '14px'}}>
                                        {fileError}
                                    </div>
                                )}
                                {selectedFile && field.name === 'confirmationEmail' && (
                                    <div className="text-success mt-1" style={{fontSize: '14px'}}>
                                        Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                                    </div>
                                )}
                            </div>
                        ) : field.type === 'number' ? (
                            <div className="input-group">
                                {field.icon && (
                                    <span className="input-group-text">
                                      <i style={{fontSize:'18px'}} className={field.icon}></i>
                                    </span>
                                )}
                                <input
                                    type="number"
                                    className="form-control"
                                    id={field.name}
                                    name={field.name}
                                    value={formValues[field.name]}
                                    onChange={(e) => handleChange(field.name, e.target.value)}
                                    min={field.min}
                                    max={field.max}
                                    step={"any"}
                                    placeholder={field.placeholder}
                                    required={field.required && shouldShowField(field)} // Only required if visible
                                />
                            </div>
                        ) : (
                            <div className="input-group">
                                {field.icon && (
                                    <span className="input-group-text">
                                      <i style={{fontSize:'18px'}} className={field.icon}></i>
                                    </span>
                                )}
                                <input
                                    type={field.type === 'tel' ? 'tel' : 'text'} // Use 'tel' type for phone numbers
                                    className="form-control"
                                    id={field.name}
                                    name={field.name}
                                    value={formValues[field.name]}
                                    onChange={(e) => handleChange(field.name, e.target.value)}
                                    placeholder={field.placeholder}
                                    required={field.required && shouldShowField(field)} // Only required if visible
                                />
                            </div>
                        )}
                    </div>
                ))}

                {/* Remove the standalone file upload section since it's now handled in the fields array */}

                <button type="submit" className="btn btn-primary">{submitBtnTxt}</button>
            </form>
        </div>
    );
};

export default DynamicFormComponent;